import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import iconEdit from "images/icons/edit.svg";
import iconCancel from "images/icons/cancel.svg";
import iconConfirm from "images/icons/confirm.svg";

import ChangePasswordDialog from "components/ChangePasswordDialog";
import Button from "components/Button";
import Main from "components/Main";
import { Link } from "react-router-dom";

import useFetchApi from "hooks/useFetchApi";
import { useConfirm } from "components/ConfirmDialog";
import { useMenu } from "hooks/useMenuFooter";
import { useConfig } from "providers/ConfigProvider";
import { useAuth } from "providers/AuthProvider";
import useSWR, { mutate } from "swr";
import { toast } from "react-toastify";

const DeleteAccount = () => {
  const { deleteUser } = useAuth();
  const [confirmConversion, confirmConversionDialog] = useConfirm();
  return (
    <>
      <Button
        label="Supprimer mon compte"
        variant="noborder"
        color="red"
        className="underline !p-0 hidden"
        onClick={() =>
          confirmConversion({
            text: "Êtes-vous sûr de vouloir supprimer votre compte ?",
            subText: "Toute suppression est irréversible.",
            action: () => deleteUser(),
          })
        }
      />
      {confirmConversionDialog}
    </>
  );
};

const MyAccount = () => {
  const { isMobile } = useMenu();
  const { isCustomerUrl, isPartnerUrl } = useConfig(); // TODO: Cacher les CGU si partenaire
  const [fetchApi] = useFetchApi();
  const [showChangePasswordDialog, setShowChangePasswordDialog] =
    useState(false);
  const [body, setBody] = useState({ data: {} }); // Initialize with an empty data object
  // Callback to handle input changes and update body state
  const handleInputChange = (name, value) => {
    setBody((prevBody) => ({
      ...prevBody,
      data: {
        ...prevBody.data,
        [name]: value,
      },
    }));
  };
  const { isCustomer } = useAuth();
  const [editing, setEditing] = useState(false);
  // Fetch data using useSWR
  const { data } = useSWR("users/me", fetchApi);

  const onSubmit = async () => {
    document.activeElement?.blur();
    try {
      await fetchApi("user/profile", { body: body.data, method: "PUT" });
      setEditing(false);
      mutate("users/me");
      toast.success("Votre profil a bien été modifié.");
    } catch (err) {
      toast.error(
        err?.message ??
          "Une erreur est survenue lors de la mise à jour du profil.",
      );
    }
  };
  const onCancel = () => {
    setEditing(false);
    if (data) {
      const { telephone, name, firstname, email } = data;
      setBody({ data: { telephone, name, firstname, email } });
    }
  };

  // Update the body state with fetched data when it changes
  useEffect(() => {
    if (data) {
      const { telephone, name, firstname, email } = data;
      setBody({
        data: {
          telephone,
          name,
          firstname,
          email,
        },
      });
    }
  }, [data]);

  const inputs = [
    { label: "Nom", name: "name" },
    { label: "Prénom", name: "firstname" },
    { label: "Adresse mail", name: "email" },
    { label: "Téléphone", name: "telephone", maxLength: "10" },
  ];

  return (
    <>
      <Main title="Mon profil" header={isCustomer && <DeleteAccount />}>
        <div className="max-w-[544px] rounded-xl border-0.5 border-stone-400 p-5">
          <div className="flex items-center justify-between">
            <h1 className="text-main-color text-base font-semibold">
              Mon profil
            </h1>
            <div>
              {editing && (
                <div className="flex space-x-1">
                  <button onClick={onSubmit}>
                    <img src={iconConfirm} alt="confirmer" />
                  </button>
                  <button onClick={onCancel}>
                    <img src={iconCancel} alt="annuler" />
                  </button>
                </div>
              )}
              {!editing && (
                <div className="flex space-x-1">
                  <button onClick={() => setEditing(!editing)}>
                    <img src={iconEdit} alt="modifier" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {inputs.map((input) => (
            <EditableInput
              key={input.name}
              label={input.label}
              name={input.name}
              value={body.data[input.name] || ""} // Set initial value from body state
              onChange={handleInputChange} // Pass the callback function
              onSubmit={onSubmit}
              onCancel={onCancel}
              editing={editing}
              maxLength={input.maxLength}
            />
          ))}
        </div>
        <div className="mt-8 flex gap-2 flex-col items-center justify-center">
          <Button
            label="Changer mon mot de passe"
            variant="contained"
            className="text-xs md:text-sm font-semibold leading-5"
            onClick={() => setShowChangePasswordDialog(true)}
          />
          {isMobile && (
            <Link
              to="/deconnexion"
              className="text-sm font-semibold leading-5 underline"
            >
              Se déconnecter
            </Link>
          )}
        </div>
        {showChangePasswordDialog && (
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-white bg-opacity-90 flex items-center justify-center">
            <ChangePasswordDialog
              onClose={() => setShowChangePasswordDialog(false)}
            />
          </div>
        )}
      </Main>

      <div className="p-3  md:p-0 items-center justify-center md:justify-start w-full  md:relative bottom-20 md:bottom-0 flex gap-3 md:mt-2 text-xs underline">
        <p>
          <Link to="/cgu">CGU</Link>
        </p>
        <p>
          {isCustomerUrl || isPartnerUrl ? (
            <Link
              target={"_blank"}
              to="https://www.soregies.fr/particuliers/primes-et-financements/conditions-generales-de-ventes-cgv-de-loffre-cee/"
            >
              CGV
            </Link>
          ) : (
            <Link to="/cgv">CGV</Link>
          )}
        </p>
        <p>
          <Link to="/policy">Politique de confidentialité</Link>
        </p>
        <p>
          {isCustomerUrl || isPartnerUrl ? (
            <Link
              target={"_blank"}
              to="https://www.soregies.fr/mentions-legales-simulateur-espace-beneficiaire-cee/"
            >
              Mentions légales
            </Link>
          ) : (
            <Link to="/mention">Mentions légales</Link>
          )}
        </p>
      </div>
    </>
  );
};

const EditableInput = ({
  label,
  name,
  value,
  editing,
  onChange,
  maxLength,
}) => {
  const handleInputOnChange = (event) => {
    const { name, value } = event.target;
    onChange(name, value); // Call the callback function to update the body state
  };

  return (
    <div className="text-sm mt-4 flex">
      <label className="w-[120px]">{label}: </label>
      {editing ? (
        <input
          type="text"
          name={name}
          value={value}
          onChange={handleInputOnChange}
          className="w-[350px] ml-1 border-0.5 border-stone-400 px-1 py-1"
          maxLength={maxLength || 100}
        />
      ) : (
        <span>{value}</span>
      )}
    </div>
  );
};
EditableInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  editing: PropTypes.bool,
  maxLength: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default MyAccount;
